import { useMemo } from 'react';
import useNewPricing from '@/components/Modals/CreateAccount/hooks/useNewPricing';
import { Skeleton, StackingCardGroup } from '@music-vine/cadence/ui';
import { CardPlan } from './CardPlan';
import type { CardPlanProps } from '@/components/Modals/SignUp/components/CardPlan';
import type { CreateAccountType } from '@/components/Modals/SignUp/types';
import type { Plan } from '@/fixtures/plans';
import { getPlanCardContent } from '../constants/planCardContent';
export interface CardPlansProps {
  excludedPlans?: CreateAccountType[];
  filter?: (plan: CardPlanProps) => boolean;
  onSelectedPlanChange: (planType: Plan['name']) => void;
  selectedPlan: Plan['name'] | null;
}
const initialExcludedPlans: CreateAccountType[] = [];
export const CardPlansAll = ({
  selectedPlan,
  onSelectedPlanChange,
  excludedPlans = initialExcludedPlans,
  filter
}: CardPlansProps) => {
  const {
    currency,
    period,
    premium,
    pro,
    business,
    essentials,
    free,
    isLoading
  } = useNewPricing();
  const planData = useMemo(() => {
    if (isLoading || !premium || !pro || !currency) {
      return [];
    }
    const plans: (CardPlanProps & {
      value: string;
    })[] = [];

    // Add free plan
    if (free) {
      plans.push({
        id: 'Free',
        value: 'Free',
        title: (free.title || 'Free') as Plan['name'],
        description: free.description || '',
        isPopular: free.is_popular,
        benefits: (free.benefits || []).join('\n'),
        collapsible: true
      });
    }
    const displayAmount = (plan: {
      monthly: {
        monthlyPrice?: string | null;
      };
      yearly: {
        monthlyEquivalent: {
          monthlyEquivalentPrice?: string | null;
        };
      };
    }) => period === 'monthly' ? plan.monthly.monthlyPrice ?? '0.00' : plan.yearly.monthlyEquivalent.monthlyEquivalentPrice ?? '0.00';

    // Paid cards: licensing-led descriptions, library icon chips and per-plan
    // tick lists from one shared source, with Pro highlighted as Popular
    // (DEV-6406).
    const paidPlans = [{
      id: 'Essentials',
      pricing: essentials
    }, {
      id: 'Creator',
      pricing: premium
    }, {
      id: 'Pro',
      pricing: pro
    }, {
      id: 'Business',
      pricing: business
    }] as const;
    for (const {
      id,
      pricing
    } of paidPlans) {
      if (!pricing) {
        continue;
      }
      plans.push({
        id,
        value: id,
        title: (pricing.title || id) as Plan['name'],
        subtitle: {
          currency,
          amount: displayAmount(pricing),
          period: 'monthly'
        },
        collapsible: true,
        ...getPlanCardContent(id),
        isPopular: id === 'Pro',
        badgeLabel: id === 'Pro' ? 'Popular' : undefined
      });
    }
    return plans.filter(plan => !excludedPlans.includes(plan.id as unknown as CreateAccountType));
  }, [isLoading, premium, pro, business, currency, free, essentials, period, excludedPlans]);
  const filteredPlanData = useMemo(() => {
    if (!filter) {
      return planData;
    }
    return planData.filter(filter);
  }, [planData, filter]);
  const normalizedSelectedPlan = selectedPlan;
  const handleValueChange = (value: string) => {
    onSelectedPlanChange(value as Plan['name']);
  };
  if (isLoading || !filteredPlanData.length) {
    return <div className="flex flex-col gap-4">
        <Skeleton className="h-[90px] w-full" items={4} />
      </div>;
  }
  return <StackingCardGroup aria-label="Choose your subscription plan" onValueChange={handleValueChange} value={normalizedSelectedPlan ?? undefined} data-sentry-element="StackingCardGroup" data-sentry-component="CardPlansAll" data-sentry-source-file="CardPlansAll.tsx">
      {filteredPlanData.map(plan => <CardPlan key={plan.id} {...plan} selectable value={plan.value} />)}
    </StackingCardGroup>;
};